import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Checkbox, Form, Input, Layout, List, message, Modal, Typography } from "antd";
import Register from "./Register";
import NetworkApi from "../../network/networkApi";
import moment from "moment";
import {
  UserOutlined,
  LockOutlined,
  FileDoneOutlined,
  QrcodeOutlined,
  FilePdfFilled,
  ExclamationCircleFilled,
  TwitterOutlined,
  FacebookFilled,
  SafetyOutlined,
  SearchOutlined,
  LinkOutlined,
  PhoneOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { USER_ROLE } from "../../enums/user.enum";
import "./login.css";
import License from "../lib/License";
import config from "../../config";
import { FRONTEND_VERSION } from "../../version";

/* ── Data ── */

const manualData = [
  {
    number: "1",
    name: "Систем ашиглах заавар",
    link: "https://drive.google.com/drive/u/2/folders/1_zX7qnYbNO5vmG39RsqtwCSA3m895Qr-",
  },
  {
    number: "2",
    name: "Бүртгүүлэх заавар (видео)",
    link: "https://www.youtube.com/watch?v=ZGRx2nB56Lg",
  },
];

/* ── Tab keys ── */
type TabKey = "rules" | "certificate" | "manual";

/* ── Component ── */

const Login = () => {
  const history = useHistory();
  const [showRegister, changeShowRegister] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("rules");
  const [login, changeLogin] = useState({ name: "", password: "" });
  const [loginForm] = Form.useForm();
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [rulesData, setRulesData] = useState<any[]>([]);
  const [selectedRule, setSelectedRule] = useState<any>(null);
  const [backendVersion, setBackendVersion] = useState("-");

  useEffect(() => {
    const rememberedName = window.localStorage.getItem("rememberedLoginName");
    const shouldRemember = window.localStorage.getItem("rememberLogin") !== "false";
    loginForm.setFieldsValue({ name: rememberedName || "", remember: shouldRemember });
    if (rememberedName) changeLogin((current) => ({ ...current, name: rememberedName }));

    NetworkApi.requestGet("/api/login-rule/active").then((response) => {
      if (response.resultCode === 200 && Array.isArray(response.result)) {
        setRulesData(response.result);
      }
    });
    NetworkApi.requestGet("/api/announcement/active").then((response) => {
      if (response.resultCode === 200 && Array.isArray(response.result)) {
        setAnnouncements(response.result);
        if (response.result.length > 0) setAnnouncementOpen(true);
      }
    });
    NetworkApi.requestGet("/api/version").then((response) => {
      if (response.resultCode === 200 && response.result?.backendVersion) {
        setBackendVersion(response.result.backendVersion);
      }
    });
  }, []);

  /* Certificate check state */
  const [certId, setCertId] = useState("");
  const [certVisible, setCertVisible] = useState(false);

  const handleClick = useCallback(async (values: any) => {
    const credentials = { name: values.name, password: values.password };
    let result = await NetworkApi.requestPost("/api/auth/login", credentials);
    if (result.resultCode == 200) {
      if (values.remember) {
        window.localStorage.setItem("rememberedLoginName", values.name);
        window.localStorage.setItem("rememberLogin", "true");
      } else {
        window.localStorage.removeItem("rememberedLoginName");
        window.localStorage.setItem("rememberLogin", "false");
      }
      message.success("Системд амжилттай нэвтэрлээ...");
      window.localStorage.setItem("announcement", JSON.stringify(true));
      if (result.result?.user?.role?.cd === USER_ROLE.WAREHOUSE_SPECIALIST) {
        window.location.href = "/wm/warehouse";
      } else {
        window.location.href = "/";
      }
    } else {
      message.error(result.message || "Системийн алдаа");
    }
  }, []);

  const onFinish = (values: any) => handleClick(values);

  /* ── Tab content renderers ── */

  const renderRules = () => (
    <>
      <div className="login-tab-content-header">
        <div className="header-dot" />
        <h4>Дүрэм журам, тушаал шийдвэр</h4>
      </div>
      <div className="login-data-list">
        {rulesData.map((item, index) => (
          <div className="login-data-item" key={item.id} onClick={() => setSelectedRule(item)}>
            <div className="item-number">{index + 1}</div>
            <div className="item-content">
              {item.imageName && <img className="item-image" src={`${config.get("apiUrl")}/tmp/${item.imageName}`} alt={item.title} />}
              {item.information && <div className="item-information">{item.information}</div>}
              <div className="item-title">{item.title}</div>
              <div className="item-meta">
                <span className="item-date">{moment(item.documentDate).format("YYYY.MM.DD")}</span>
                <span className="item-link">
                  <a href={item.link} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>
                    <LinkOutlined /> Үзэх
                  </a>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Modal
        visible={!!selectedRule}
        title="Дүрэм журам, тушаал шийдвэр"
        centered
        width={760}
        onCancel={() => setSelectedRule(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedRule(null)}>Хаах</Button>,
          <Button key="view" type="primary" href={selectedRule?.link} target="_blank" rel="noopener noreferrer">Үзэх</Button>,
        ]}
      >
        {selectedRule && <div className="login-rule-detail">
          {selectedRule.imageName && <img src={`${config.get("apiUrl")}/tmp/${selectedRule.imageName}`} alt={selectedRule.title} className="login-rule-detail-image" />}
          {selectedRule.information && <div className="login-rule-detail-information">{selectedRule.information}</div>}
          <Typography.Title level={3}>{selectedRule.title}</Typography.Title>
          <div className="login-rule-detail-date">Нийтлэгдсэн огноо: {moment(selectedRule.documentDate).format("YYYY.MM.DD")}</div>
        </div>}
      </Modal>
    </>
  );

  const renderCertificate = () => (
    <>
      <div className="login-tab-content-header">
        <div className="header-dot" style={{ background: "#2E8B7A" }} />
        <h4>Гарал үүслийн гэрчилгээ лавлах</h4>
      </div>
      <div className="login-cert-check">
        <h3>
          <QrcodeOutlined /> ГҮГ шалгах
        </h3>
        <div className="cert-input-row">
          <Input
            value={certId}
            onChange={(e) => setCertId(e.target.value)}
            placeholder="Гэрчилгээний дугаар оруулах"
            onPressEnter={() => certId && setCertVisible(true)}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => certId && setCertVisible(true)}
          >
            Шалгах
          </Button>
        </div>
      </div>
      <Modal
        width={1240}
        centered
        visible={certVisible}
        onCancel={() => setCertVisible(false)}
        footer={[
          <Button key="close" onClick={() => setCertVisible(false)}>
            Хаах
          </Button>,
        ]}
      >
        <License
          isSmall={false}
          id={certVisible ? certId : ""}
          key="PrintLicense"
        />
      </Modal>
    </>
  );

  const renderManual = () => (
    <>
      <div className="login-tab-content-header">
        <div className="header-dot" style={{ background: "#B8860B" }} />
        <h4>Гарын авлага, заавар</h4>
      </div>
      <div className="login-data-list">
        {manualData.map((item) => (
          <div className="login-data-item" key={item.number}>
            <div className="item-number">{item.number}</div>
            <div className="item-content">
              <div className="item-title">{item.name}</div>
              <div className="item-meta">
                <span className="item-link">
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    <LinkOutlined /> Үзэх
                  </a>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const tabContent: Record<TabKey, () => JSX.Element> = {
    rules: renderRules,
    certificate: renderCertificate,
    manual: renderManual,
  };

  return (
    <Layout className="login-layout">
      {/* ══════ Header ══════ */}
      <header className="login-header">
        <div className="login-header-left">
          <img
            src="/logosingle.png"
            alt="ХХААХҮЯ"
            className="login-header-logo"
          />
          <div className="login-header-divider" />
          <div>
            <h1 className="login-header-title">
              Тариалан үйлдвэрлэлийн нэгдсэн систем
            </h1>
            <div className="login-header-subtitle">
              Хүнс, хөдөө аж ахуй, хөнгөн үйлдвэрийн яам
            </div>
          </div>
        </div>
      </header>
      <div className="login-gold-bar" />

      {/* ══════ Main ══════ */}
      <div className="login-main">
        {/* ── Left: Info Panel ── */}
        <div className="login-info-panel">
          {/* Custom tab buttons */}
          <div className="login-custom-tabs">
            <div
              className={`login-custom-tab ${activeTab === "rules" ? "active" : ""}`}
              onClick={() => setActiveTab("rules")}
            >
              <div className="tab-icon icon-rules">
                <FileDoneOutlined />
              </div>
              <span className="tab-text">Дүрэм журам</span>
            </div>
            <div
              className={`login-custom-tab ${activeTab === "certificate" ? "active" : ""}`}
              onClick={() => setActiveTab("certificate")}
            >
              <div className="tab-icon icon-cert">
                <QrcodeOutlined />
              </div>
              <span className="tab-text">ГҮГ лавлах</span>
            </div>
            <div
              className={`login-custom-tab ${activeTab === "manual" ? "active" : ""}`}
              onClick={() => setActiveTab("manual")}
            >
              <div className="tab-icon icon-manual">
                <FilePdfFilled />
              </div>
              <span className="tab-text">Гарын авлага</span>
            </div>
          </div>

          {/* Tab content */}
          <div className="login-tab-content">{tabContent[activeTab]()}</div>
        </div>

        {/* ── Right: Login Form ── */}
        <div className="login-form-panel">
          <div className="login-form-card">
            <div className="login-form-header">
              <div className="login-form-emblem">
                <SafetyOutlined />
              </div>
              <h2 className="login-form-title">Нэвтрэх</h2>
              <p className="login-form-desc">
                Системд нэвтрэхийн тулд мэдээллээ оруулна уу
              </p>
            </div>

            <Form
              name="login_form"
              form={loginForm}
              initialValues={{ remember: true }}
              onFinish={onFinish}
            >
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Хэрэглэгчийн нэрээ оруулна уу!",
                  },
                ]}
              >
                <Input
                  size="large"
                  value={login.name}
                  onChange={(t) =>
                    changeLogin({ ...login, name: t.target.value })
                  }
                  placeholder="Нэвтрэх нэр"
                  prefix={<UserOutlined style={{ color: "#A0AEC0" }} />}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: "Нууц үгээ оруулна уу!" }]}
              >
                <Input.Password
                  size="large"
                  value={login.password}
                  onChange={(t) =>
                    changeLogin({ ...login, password: t.target.value })
                  }
                  placeholder="Нууц үг"
                  prefix={<LockOutlined style={{ color: "#A0AEC0" }} />}
                />
              </Form.Item>

              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>Намайг сана</Checkbox>
              </Form.Item>

              <Form.Item style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                >
                  Нэвтрэх
                </Button>
              </Form.Item>

              <div className="login-form-links">
                <Button
                  type="link"
                  onClick={() => history.push("/forgot/password")}
                >
                  Нууц үг мартсан
                </Button>
                <Button type="link" onClick={() => changeShowRegister(true)}>
                  Бүртгүүлэх
                </Button>
              </div>
            </Form>
          </div>

          {/* Web links */}
          <div className="login-weblinks">
            <div className="login-weblinks-title">Холбоосууд</div>
            <a
              href="https://mofa.gov.mn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Хүнс, хөдөө аж ахуй, хөнгөн үйлдвэрийн яам
            </a>
            <a
              href="http://khaads.mn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Хөдөө аж ахуйн дэмжлэгийн сан
            </a>
          </div>
        </div>
      </div>

      {/* ══════ Contact Info ══════ */}
      <div className="login-contact-section">
        <div className="login-contact-inner">
          <div className="login-contact-header">
            <BankOutlined className="login-contact-header-icon" />
            <div>
              <h3 className="login-contact-title">Холбоо барих</h3>
              <p className="login-contact-org">
                Газар тариалангийн бодлогын хэрэгжилтийг зохицуулах газар
              </p>
            </div>
          </div>
          <div className="login-contact-grid">
            {/* Phone 1 */}
            <div className="login-contact-card">
              <div className="login-contact-phone">
                <PhoneOutlined />
                <a href="tel:51262713">5126-2713</a>
              </div>
              <ul className="login-contact-list">
                <li>Үр тариа, тос, тэжээлийн таримлын үйлдвэрлэл, хангамж</li>
                <li>Газар тариалангийн үйлдвэрлэл, органик бүтээгдэхүүн</li>
              </ul>
            </div>
            {/* Phone 2 */}
            <div className="login-contact-card">
              <div className="login-contact-phone">
                <PhoneOutlined />
                <a href="tel:51263408">5126-3408</a>
              </div>
              <ul className="login-contact-list">
                <li>Төмс, хүнсний ногооны үйлдвэрлэл</li>
                <li>Тариалангийн газрын ашиглалт, хөрсний үржил шим</li>
                <li>Жимс жимсгэнэ, ашигт таримлын үйлдвэрлэл</li>
                <li>Таримал ургамлын үр, сорт</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ Footer ══════ */}
      <footer className="login-footer">
        <div className="login-footer-left">
          <img
            src="/logosingle.png"
            alt="ХХААХҮЯ"
            className="login-footer-logo"
          />
          <p className="login-footer-text">
            Зохиогчийн эрх ©{new Date().getFullYear()} Хүнс, хөдөө аж ахуй,
            хөнгөн үйлдвэрийн яам
          </p>
          <p className="login-version">
            Frontend: {FRONTEND_VERSION} · Backend: {backendVersion}
          </p>
        </div>
        <div className="login-footer-social">
          <a
            href="https://twitter.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwitterOutlined />
          </a>
          <a
            href="https://www.facebook.com/mofagovmn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FacebookFilled />
          </a>
        </div>
      </footer>

      <Modal
        title="Мэдэгдэл"
        open={announcementOpen}
        onCancel={() => setAnnouncementOpen(false)}
        footer={<Button type="primary" onClick={() => setAnnouncementOpen(false)}>Ойлголоо</Button>}
        width={620}
      >
        <List
          dataSource={announcements}
          split={false}
          renderItem={(item) => (
            <List.Item style={{ padding: "8px 0" }}>
              <div style={{ width: "100%" }}>
                <div style={{ textAlign: "center", color: "#d48806", marginBottom: 14 }}>
                  <ExclamationCircleFilled style={{ fontSize: 72 }} />
                  <div style={{ fontSize: 32, fontWeight: 700, marginTop: 8 }}>Анхааруулга</div>
                </div>
                <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 12, padding: 24 }}>
                  <Typography.Text strong style={{ display: "block", fontSize: 20, marginBottom: 12 }}>{item.title}</Typography.Text>
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 16, lineHeight: 1.7 }}>{item.content}</div>
                  {Array.isArray(item.attachments) && item.attachments.map((file: any) => (
                    <div key={file.name} style={{ marginTop: 14 }}>
                      <a href={`${config.get("apiUrl")}/tmp/${file.name}`} target="_blank" rel="noreferrer">{file.originalName || file.name}</a>
                    </div>
                  ))}
                  <div style={{ marginTop: 18, color: "#595959" }}>Оруулсан огноо: {item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD HH:mm") : "-"}</div>
                  <div style={{ color: "#595959" }}>Байгууллага: {item.organizationName}</div>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      <Register visible={showRegister} hide={() => changeShowRegister(false)} />
    </Layout>
  );
};

export default Login;
